import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useTickets } from "../../hooks/useTickets";
import { TicketPriority } from "../../service/CallCenterApi";
import { getOfficeUsers } from "../../service/CallCenterCalls";
import {
  getMissingRequiredHeaders,
  OPTIONAL_TICKET_UPLOAD_HEADERS,
  readUploadHeaders,
  REQUIRED_TICKET_UPLOAD_HEADERS,
} from "../../components/call-center/ticketUploadUtils";
import CallCenterNav from "../../components/call-center/CallCenterNav";

const ticketSchema = Yup.object({
  accountNo: Yup.string().trim().required("Account number is required"),
  priority: Yup.string().oneOf(Object.values(TicketPriority)).required("Priority is required"),
});

const initialTicket = {
  accountNo: "",
  priority: TicketPriority.MEDIUM,
  remarks: "",
  assignedTo: "",
};

const acceptedTypes = [".csv", ".xls", ".xlsx"];

const CallCenterAgentWorkspacePage = () => {
  const ticketsApi = useTickets();
  const [uploadFile, setUploadFile] = useState(null);
  const [headerPreview, setHeaderPreview] = useState([]);
  const [missingHeaders, setMissingHeaders] = useState([]);
  const [bulkPriority, setBulkPriority] = useState(TicketPriority.MEDIUM);
  const [officeUsers, setOfficeUsers] = useState([]);
  const [officeUsersLoading, setOfficeUsersLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadOfficeUsers = async () => {
      try {
        setOfficeUsersLoading(true);
        const users = await getOfficeUsers();
        if (!cancelled) {
          setOfficeUsers(Array.isArray(users) ? users : []);
        }
      } catch (error) {
        if (!cancelled) {
          setOfficeUsers([]);
          toast.error(error.message || "Failed to load office users");
        }
      } finally {
        if (!cancelled) {
          setOfficeUsersLoading(false);
        }
      }
    };
    loadOfficeUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreateTicket = async (values, { resetForm }) => {
    try {
      const payload = {
        accountNo: values.accountNo,
        priority: values.priority,
        remarks: values.remarks || undefined,
        assignedTo: values.assignedTo ? Number(values.assignedTo) : undefined,
      };
      await ticketsApi.create(payload);
      toast.success("Ticket created successfully");
      resetForm();
    } catch (error) {
      toast.error(error.message || "Failed to create ticket");
    }
  };

  const onFileSelected = async (event) => {
    const selected = event.target.files?.[0];
    setUploadFile(selected || null);
    setHeaderPreview([]);
    setMissingHeaders([]);
    if (!selected) return;

    const name = selected.name.toLowerCase();
    const isValidType = acceptedTypes.some((type) => name.endsWith(type));
    if (!isValidType) {
      toast.error("Invalid file type. Please upload CSV/XLS/XLSX");
      return;
    }

    try {
      const headers = await readUploadHeaders(selected);
      setHeaderPreview(headers);
      setMissingHeaders(getMissingRequiredHeaders(headers));
    } catch {
      toast.error("Failed to parse file structure");
    }
  };

  const submitBulkUpload = async () => {
    if (!uploadFile) {
      toast.error("No file selected");
      return;
    }
    if (!bulkPriority) {
      toast.error("Please select a bulk priority");
      return;
    }
    if (missingHeaders.length > 0) {
      toast.error("Header validation failed");
      return;
    }
    try {
      await ticketsApi.uploadBulk({
        file: uploadFile,
        priority: bulkPriority,
        stopOnError: false,
      });
      toast.success("Bulk upload processed");
      setUploadFile(null);
      setHeaderPreview([]);
    } catch (error) {
      toast.error(error.message || "Bulk upload failed");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className=" mx-auto">
        <header className="mb-8">
          <div className="flex justify-between items-end">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Agent Workspace</h2>
              <p className="text-gray-500">Log interactions and manage bulk data</p>
            </div>
            <Link 
              to="/call-center/tickets" 
              className="text-sm font-semibold text-[#236DB4] hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors"
            >
              ← Back
            </Link>
          </div>
          <div className="mt-4">
            <CallCenterNav />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Manual Entry Form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100 bg-gray-50/50">
                <h3 className="font-bold text-gray-700 flex items-center gap-2">
                  <span className="w-2 h-5 bg-[#236DB4] rounded-full"></span>
                  Manual Ticket Entry
                </h3>
              </div>
              
              <Formik initialValues={initialTicket} validationSchema={ticketSchema} onSubmit={handleCreateTicket}>
                {({ errors, touched }) => (
                  <Form className="p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-1">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Account Number</label>
                        <Field name="accountNo" placeholder="ACC-789" className={`w-full p-2.5 bg-gray-50 border rounded-lg outline-none transition-all focus:ring-2 focus:ring-blue-500 ${errors.accountNo && touched.accountNo ? 'border-red-500' : 'border-gray-200'}`} />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Priority</label>
                        <Field as="select" name="priority" className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500">
                          {Object.values(TicketPriority).map((p) => <option key={p} value={p}>{p}</option>)}
                        </Field>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Assigned To</label>
                        <Field as="select" name="assignedTo" className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" disabled={officeUsersLoading}>
                          <option value="">{officeUsersLoading ? "Loading users..." : "Select user (optional)"}</option>
                          {officeUsers.map((user) => (
                            <option key={user.userId} value={String(user.userId)}>
                              {user.name || user.email || `User ${user.userId}`} ({user.userId}){user.agentExtension ? ` • Ext ${user.agentExtension}` : ""}
                            </option>
                          ))}
                        </Field>
                      </div>

                      <div className="md:col-span-2 space-y-1">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Remarks / Interaction Notes</label>
                        <Field as="textarea" name="remarks" rows="3" className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>

                    <div className="pt-4">
                      <button type="submit" className="w-full md:w-auto px-10 py-3 bg-gray-900 hover:bg-black text-white font-bold rounded-xl shadow-lg transition-all active:scale-[0.98]">
                        Create Ticket
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>

          {/* Bulk Upload Utility */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-700 mb-2">Bulk Import</h3>
              <p className="text-xs text-gray-500 mb-6 leading-relaxed">
                Upload multiple tickets via CSV or Excel. Required columns:<br/>
                <code className="text-[#236DB4] bg-blue-50 px-1 rounded">{REQUIRED_TICKET_UPLOAD_HEADERS.join(", ")}</code><br/>
                Request-level field applied to all rows: <code className="text-[#236DB4] bg-blue-50 px-1 rounded">priority</code>.<br/>
                <span className="text-amber-700">agentName must exactly match a project office user name.</span><br/>
                Optional: <code className="text-[#236DB4] bg-blue-50 px-1 rounded">{OPTIONAL_TICKET_UPLOAD_HEADERS.join(", ")}</code>
              </p>

              <div className="grid grid-cols-1 gap-3 mb-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Bulk Priority</label>
                  <select
                    value={bulkPriority}
                    onChange={(event) => setBulkPriority(event.target.value)}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.values(TicketPriority).map((priority) => (
                      <option key={`bulk-${priority}`} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={`relative border-2 border-dashed rounded-2xl p-8 transition-all text-center ${uploadFile ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`}>
                <input 
                  type="file" 
                  accept={acceptedTypes.join(",")} 
                  onChange={onFileSelected}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="space-y-2 pointer-events-none">
                  <div className="mx-auto w-10 h-10 bg-white rounded-full shadow-sm flex items-center justify-center text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div className="text-sm font-semibold text-gray-600">
                    {uploadFile ? uploadFile.name : "Drop file here or click to browse"}
                  </div>
                  <div className="text-[10px] text-gray-400 uppercase font-bold">CSV, XLS, XLSX</div>
                </div>
              </div>

              {/* Validation Feedback */}
              {(headerPreview.length > 0 || missingHeaders.length > 0) && (
                <div className="mt-6 p-4 rounded-xl border bg-gray-50 space-y-3">
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Validation Result</h4>
                  
                  {missingHeaders.length > 0 ? (
                    <div className="flex items-start gap-2 text-red-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <div className="text-xs font-medium">Missing: {missingHeaders.join(", ")}</div>
                    </div>
                  ) : headerPreview.length > 0 && (
                    <div className="flex items-start gap-2 text-green-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      <div className="text-xs font-medium">Headers validated successfully</div>
                    </div>
                  )}
                  
                  {headerPreview.length > 0 && (
                    <div className="text-[10px] text-gray-500 font-mono break-all leading-relaxed bg-white p-2 rounded border">
                      Found: {headerPreview.join(" | ")}
                    </div>
                  )}
                </div>
              )}

              <button 
                type="button" 
                onClick={submitBulkUpload}
                disabled={!uploadFile || missingHeaders.length > 0}
                className="w-full mt-6 py-3 bg-[#236DB4] hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-100 transition-all disabled:bg-gray-200 disabled:shadow-none"
              >
                Process Bulk Upload
              </button>
            </div>
            
            <div className="bg-amber-50 rounded-2xl border border-amber-100 p-6">
              <div className="flex gap-3">
                <span className="text-xl">💡</span>
                <div>
                  <h4 className="text-sm font-bold text-amber-900">Agent Tip</h4>
                  <p className="text-xs text-amber-800/80 leading-relaxed mt-1">
                    Add clear interaction notes in remarks to keep ticket timelines useful during manual call tracking.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallCenterAgentWorkspacePage;